import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import {
    clearContractDetails,
    loadClaimEventStatuses,
    loadClaimEventStatusReasons,
    loadClaimEventTypes,
    loadContractDetails,
    loadContractDetailsForContractIds
} from '../../store/directusService';
import { CONTRACT_DETAILS_ERROR } from '../../utils/formValidation/loadingErrorMessageConstants';
import { hasRole, MANAGER, PRAP, QUALITY, SUPERUSER } from '../../utils/userRoles';
import LoadingSpinner from '../ui/LoadingSpinner';

import ClaimTableManagement from './claim/ClaimTableManagement';
import ValidationTableManagement from './validation/ValidationTableManagement';

import claims from './claimStyles/claims.module.css';

const validationRoles = [MANAGER, QUALITY, SUPERUSER];
const claimRoles = [PRAP];
const allContractRoles = [PRAP, QUALITY, SUPERUSER];
const limitedContractRoles = [MANAGER];

const Claims = () => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [tab, setTab] = useState(0);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const claimEventStatuses = useSelector(
        (state) => state.entities.directusService.claimEventStatuses
    );
    const claimEventStatusReasons = useSelector(
        (state) => state.entities.directusService.claimEventStatusReasons
    );
    const claimEventTypes = useSelector((state) => state.entities.directusService.claimEventTypes);
    const contractDetails = useSelector((state) => state.entities.directusService.contractDetails);

    // USE EFFECTS
    useEffect(() => {
        dispatch(clearContractDetails());
        dispatch(loadClaimEventStatuses());
        dispatch(loadClaimEventStatusReasons());
        dispatch(loadClaimEventTypes());
    }, []);

    useEffect(() => {
        if (!loggedInUser || Object.keys(loggedInUser).length < 1) return;
        const { roles = [], contractIds = [] } = loggedInUser;

        if (hasRole(allContractRoles, roles)) dispatch(loadContractDetails());
        else if (hasRole(limitedContractRoles, roles))
            dispatch(loadContractDetailsForContractIds(contractIds));

        if (hasRole(validationRoles, roles)) setTab(0);
        else if (hasRole(claimRoles, roles)) setTab(1);
    }, [loggedInUser.id]);

    // HELPER FNS
    const loadingError = () => {
        if (claimEventStatuses?.length < 1) return 'No claim event statuses';
        if (claimEventStatusReasons?.length < 1) return 'No claim event status reasons';
        if (claimEventTypes?.length < 1) return 'No claim event types';
        if (contractDetails?.length < 1) return CONTRACT_DETAILS_ERROR;
    };

    // EVENT HANDLERS
    const onChange = (e, tab) => setTab(tab);

    // RENDER
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div>
            <div className={claims.navBar}>
                <Tabs value={tab} onChange={onChange} aria-label="icon tabs example">
                    <Tab
                        icon={<FactCheckIcon />}
                        iconPosition="end"
                        label="Validations"
                        aria-label="validations"
                        className={claims.tab}
                    />
                    <Tab
                        icon={<AccountBalanceIcon />}
                        iconPosition="end"
                        label="Claims"
                        aria-label="claims"
                        className={claims.tab}
                    />
                </Tabs>
            </div>
            <div className={claims.claimsContainer}>
                {tab === 0 && <ValidationTableManagement />}
                {tab === 1 && <ClaimTableManagement />}
            </div>
        </div>
    );
};

export default Claims;
